/**
 * PB Navigation — mega menu click-to-open, tab switching, carousel, mobile drawer
 *
 * Strategy: the mega menu, backdrop, and mobile drawer are teleported to <body>
 * on init so that parent stacking contexts (Shopify section group, sticky promo
 * bar) cannot clip or cover them. The mega is then positioned from the header
 * bottom edge.
 */
(function () {
  function setMegaA11yState(mega, backdrop, shopTrigger, isOpen) {
    const focusableSelector =
      'a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

    [mega, backdrop].forEach((el) => {
      if (!el) return;
      el.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
      if (isOpen) el.removeAttribute('inert');
      else el.setAttribute('inert', '');

      el.querySelectorAll(focusableSelector).forEach((node) => {
        if (!isOpen) {
          if (node.dataset.pbNavTabindex === undefined) {
            node.dataset.pbNavTabindex = node.getAttribute('tabindex') ?? '';
          }
          node.setAttribute('tabindex', '-1');
          return;
        }
        if (node.dataset.pbNavTabindex !== undefined) {
          const prev = node.dataset.pbNavTabindex;
          if (prev) node.setAttribute('tabindex', prev);
          else node.removeAttribute('tabindex');
          delete node.dataset.pbNavTabindex;
        }
      });
    });

    if (shopTrigger) shopTrigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  }

  function isNavStickyEnabled() {
    const navRoot = document.querySelector('[data-pb-nav]');
    return !navRoot || navRoot.dataset.pbNavSticky !== 'false';
  }

  function setNavStickyHeight() {
    const section = document.querySelector('.pb-nav-header-section');
    if (!section) return;

    const height = Math.round(section.getBoundingClientRect().height);
    if (height <= 0) return;

    document.documentElement.style.setProperty('--header-height', `${height}px`);

    if (!isNavStickyEnabled()) {
      document.documentElement.style.setProperty('--pb-nav-sticky-height', '0px');
      return;
    }

    document.documentElement.style.setProperty('--pb-nav-sticky-height', `${height}px`);
  }

  function watchNavStickyHeight() {
    setNavStickyHeight();
    window.addEventListener('resize', setNavStickyHeight, { passive: true });
    const section = document.querySelector('.pb-nav-header-section');
    if (section && typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(setNavStickyHeight).observe(section);
    }
  }

  function getNavBottom() {
    const wrapper = document.querySelector('.pb-nav__wrapper');
    if (wrapper) {
      const bottom = wrapper.getBoundingClientRect().bottom;
      if (bottom > 0) return bottom;
    }
    const headerBar = document.querySelector('.pb-nav__header');
    if (headerBar) {
      const bottom = headerBar.getBoundingClientRect().bottom;
      if (bottom > 0) return bottom;
    }
    const navRoot = document.querySelector('[data-pb-nav]');
    if (navRoot) {
      const bottom = navRoot.getBoundingClientRect().bottom;
      if (bottom > 0) return bottom;
    }
    return 108;
  }

  function applyMegaPosition(mega, backdrop) {
    const top = getNavBottom();
    if (mega)     mega.style.top     = top + 'px';
    if (backdrop) backdrop.style.top = top + 'px';
  }

  /* ── Teleport ────────────────────────────────────────────────────── */
  function teleportToBody(el) {
    if (el && el.parentNode !== document.body) {
      document.body.appendChild(el);
    }
  }

  /* ── Mega menu open / close ──────────────────────────────────────── */
  function initMegaMenu(root) {
    const shopTrigger = root.querySelector('[data-pb-nav-shop-trigger]');
    const mega        = document.querySelector('[data-pb-nav-mega]');
    const backdrop    = document.querySelector('[data-pb-nav-mega-backdrop]');
    if (!shopTrigger || !mega) return;

    teleportToBody(backdrop);
    teleportToBody(mega);
    applyMegaPosition(mega, backdrop);
    setMegaA11yState(mega, backdrop, shopTrigger, false);

    let timeoutId;
    let outsideClickBound = false;

    const onDocumentClick = (e) => {
      if (mega.contains(e.target) || shopTrigger.contains(e.target)) return;
      close(0);
    };

    const bindOutsideClick = () => {
      if (outsideClickBound) return;
      document.addEventListener('click', onDocumentClick);
      outsideClickBound = true;
    };

    const unbindOutsideClick = () => {
      if (!outsideClickBound) return;
      document.removeEventListener('click', onDocumentClick);
      outsideClickBound = false;
    };

    const open = () => {
      clearTimeout(timeoutId);
      applyMegaPosition(mega, backdrop);
      mega.classList.add('is-open');
      backdrop?.classList.add('is-visible');
      shopTrigger.classList.add('is-active');
      setMegaA11yState(mega, backdrop, shopTrigger, true);
      bindOutsideClick();
      const activePanel = mega.querySelector('[data-pb-nav-panel].is-active');
      if (activePanel) {
        requestAnimationFrame(() => syncCarousel(activePanel));
      }
    };

    const close = (delay = 0, returnFocus = false) => {
      clearTimeout(timeoutId);
      const doClose = () => {
        unbindOutsideClick();
        mega.classList.remove('is-open');
        backdrop?.classList.remove('is-visible');
        shopTrigger.classList.remove('is-active');
        setMegaA11yState(mega, backdrop, shopTrigger, false);
        if (returnFocus) shopTrigger.focus();
      };
      if (delay > 0) timeoutId = setTimeout(doClose, delay);
      else doClose();
    };

    const toggleFromKeyboard = () => {
      if (mega.classList.contains('is-open')) close(0, true);
      else {
        open();
        mega.querySelector('[data-pb-nav-tab]')?.focus();
      }
    };

    const toggleFromClick = () => {
      if (mega.classList.contains('is-open')) close(0);
      else open();
    };

    shopTrigger.addEventListener('click', (e) => {
      e.preventDefault();
      toggleFromClick();
    });

    shopTrigger.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggleFromKeyboard();
        return;
      }
      if (e.key === 'ArrowDown' && !mega.classList.contains('is-open')) {
        e.preventDefault();
        open();
        mega.querySelector('[data-pb-nav-tab]')?.focus();
        return;
      }
      if (e.key === 'Escape' && mega.classList.contains('is-open')) {
        e.preventDefault();
        close(0, true);
      }
    });

    backdrop?.addEventListener('click', () => close(0));

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && mega.classList.contains('is-open')) close(0, true);
    });

    window.addEventListener(
      'scroll',
      (e) => {
        if (!mega.classList.contains('is-open')) return;
        // Ignore scroll inside the mega (product carousel, sidebar, etc.) — only close on page scroll
        if (mega.contains(e.target) || backdrop?.contains(e.target)) return;
        close(0);
      },
      { passive: true, capture: true }
    );

    window.addEventListener('resize', () => applyMegaPosition(mega, backdrop), { passive: true });
  }

  /* ── Tab switching ───────────────────────────────────────────────── */
  function initMegaTabs(megaEl) {
    const tabs   = megaEl.querySelectorAll('[data-pb-nav-tab]');
    const panels = megaEl.querySelectorAll('[data-pb-nav-panel]');
    if (!tabs.length) return;

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => activateTab(tab, tabs, panels, megaEl));
    });
  }

  function activateTab(tab, tabs, panels, megaEl) {
    const id = tab.getAttribute('data-pb-nav-tab');
    tabs.forEach((t) => t.classList.toggle('is-active', t === tab));
    panels.forEach((p) => p.classList.toggle('is-active', p.getAttribute('data-pb-nav-panel') === id));
    const active = megaEl.querySelector(`[data-pb-nav-panel="${id}"]`);
    if (active) resetCarousel(active);
  }

  /* ── Carousel ────────────────────────────────────────────────────── */
  function syncCarousel(panel) {
    const track = panel.querySelector('[data-pb-nav-carousel-track]');
    const prev  = panel.querySelector('[data-pb-nav-carousel-prev]');
    const next  = panel.querySelector('[data-pb-nav-carousel-next]');
    const nav   = panel.querySelector('[data-pb-nav-carousel-nav]');
    if (!track || !prev || !next) return;

    const hasOverflow = track.scrollWidth > track.clientWidth + 2;

    if (nav) nav.hidden = !hasOverflow;

    if (!hasOverflow) {
      prev.hidden = true;
      next.hidden = true;
      return;
    }

    prev.hidden = track.scrollLeft <= 1;
    next.hidden = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
  }

  function initCarousels(megaEl) {
    megaEl.querySelectorAll('[data-pb-nav-panel]').forEach((panel) => {
      const track = panel.querySelector('[data-pb-nav-carousel-track]');
      const prev  = panel.querySelector('[data-pb-nav-carousel-prev]');
      const next  = panel.querySelector('[data-pb-nav-carousel-next]');
      if (!track || !prev || !next) return;

      const step = () => {
        const card = track.querySelector('.pb-nav-mega__product-card');
        return card ? card.offsetWidth + 24 : 234;
      };

      const sync = () => syncCarousel(panel);

      prev.addEventListener('click', () => { track.scrollBy({ left: -step(), behavior: 'smooth' }); setTimeout(sync, 350); });
      next.addEventListener('click', () => { track.scrollBy({ left:  step(), behavior: 'smooth' }); setTimeout(sync, 350); });
      track.addEventListener('scroll', sync, { passive: true });

      if (typeof ResizeObserver !== 'undefined') {
        new ResizeObserver(sync).observe(track);
      } else {
        window.addEventListener('resize', sync, { passive: true });
      }

      sync();
    });
  }

  function resetCarousel(panel) {
    const track = panel.querySelector('[data-pb-nav-carousel-track]');
    if (!track) return;
    track.scrollLeft = 0;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => syncCarousel(panel));
    });
  }

  /* ── Mobile drawer ───────────────────────────────────────────────── */
  function initMobileDrawer(root) {
    const drawer  = root.querySelector('[data-pb-nav-drawer]');
    const openBtn = root.querySelector('[data-pb-nav-drawer-open]');
    const closeBtn = root.querySelector('[data-pb-nav-drawer-close]');
    const overlay  = root.querySelector('[data-pb-nav-drawer-overlay]');
    if (!drawer || !openBtn) return;

    teleportToBody(drawer);

    let scrollLockY = 0;

    const lockPageScroll = () => {
      scrollLockY = window.scrollY || document.documentElement.scrollTop;
      document.documentElement.classList.add('pb-nav-scroll-lock');
      document.body.classList.add('pb-nav-scroll-lock', 'overflow-hidden-mobile');
      document.body.style.setProperty('--pb-nav-scroll-lock-top', `-${scrollLockY}px`);
    };

    const unlockPageScroll = () => {
      document.documentElement.classList.remove('pb-nav-scroll-lock');
      document.body.classList.remove('pb-nav-scroll-lock', 'overflow-hidden-mobile');
      document.body.style.removeProperty('--pb-nav-scroll-lock-top');
      window.scrollTo(0, scrollLockY);
    };

    const preventBackgroundTouchMove = (e) => {
      if (e.target.closest('.pb-nav-drawer__body')) return;
      e.preventDefault();
    };

    const open = () => {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      lockPageScroll();
      document.addEventListener('touchmove', preventBackgroundTouchMove, { passive: false });
      closeBtn?.focus();
    };

    const close = () => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.removeEventListener('touchmove', preventBackgroundTouchMove);
      unlockPageScroll();
      openBtn.focus();
    };

    openBtn.addEventListener('click', open);
    closeBtn?.addEventListener('click', close);
    overlay?.addEventListener('click', close);
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && drawer.classList.contains('is-open')) close(); });
  }

  /* ── Header keyboard access ──────────────────────────────────────── */
  function ensureHeaderFocusable(root) {
    root.querySelectorAll(
      '.pb-nav__header a[href], .pb-nav__header button, .pb-nav__header summary'
    ).forEach((el) => {
      if (el.getAttribute('tabindex') === '-1') el.removeAttribute('tabindex');
    });
  }

  /* ── Boot ────────────────────────────────────────────────────────── */
  function init() {
    const root   = document.querySelector('[data-pb-nav]');
    if (!root) return;

    ensureHeaderFocusable(root);
    watchNavStickyHeight();

    const megaEl = document.querySelector('[data-pb-nav-mega]');
    initMegaMenu(root);
    if (megaEl) {
      initMegaTabs(megaEl);
      initCarousels(megaEl);
    }
    initMobileDrawer(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
